export enum KeyState {
  PRESSED,
  HELD,
  RELEASED,
  UP
}

export class Input {
  private static instance: Input;
  
  private keys: Map<string, KeyState> = new Map();
  private mousePosition: { x: number, y: number } = { x: 0, y: 0 };
  private mouseMovement: { x: number, y: number } = { x: 0, y: 0 };
  private mouseButtons: Map<number, KeyState> = new Map();
  private wheelDelta: number = 0;
  
  constructor() {
    // Set up event listeners
    window.addEventListener('keydown', this.onKeyDown.bind(this));
    window.addEventListener('keyup', this.onKeyUp.bind(this));
    window.addEventListener('mousemove', this.onMouseMove.bind(this));
    window.addEventListener('mousedown', this.onMouseDown.bind(this));
    window.addEventListener('mouseup', this.onMouseUp.bind(this));
    window.addEventListener('wheel', this.onWheel.bind(this));
    
    // Initialize mouse buttons
    for (let i = 0; i < 3; i++) {
      this.mouseButtons.set(i, KeyState.UP);
    }
    
    console.log('Input system initialized');
  }
  
  public update(): void {
    // Update key states (PRESSED -> HELD, RELEASED -> UP)
    this.keys.forEach((state, key) => {
      if (state === KeyState.PRESSED) {
        this.keys.set(key, KeyState.HELD);
      } else if (state === KeyState.RELEASED) {
        this.keys.set(key, KeyState.UP);
      }
    });
    
    // Update mouse button states
    this.mouseButtons.forEach((state, button) => {
      if (state === KeyState.PRESSED) {
        this.mouseButtons.set(button, KeyState.HELD);
      } else if (state === KeyState.RELEASED) {
        this.mouseButtons.set(button, KeyState.UP);
      }
    });
    
    // Reset wheel delta and mouse movement
    this.wheelDelta = 0;
    this.mouseMovement = { x: 0, y: 0 };
  }
  
  private onKeyDown(event: KeyboardEvent): void {
    const key = event.key.toLowerCase();
    
    if (this.keys.get(key) !== KeyState.HELD) {
      this.keys.set(key, KeyState.PRESSED);
    }
    
    // Special case for space key
    if (event.code === 'Space' && this.keys.get('space') !== KeyState.HELD) {
      this.keys.set('space', KeyState.PRESSED);
    }
    
    // Special cases for number keys
    if (event.key === '1' || event.code === 'Digit1') {
      this.keys.set('1', KeyState.PRESSED);
    }
    
    if (event.key === '2' || event.code === 'Digit2') {
      this.keys.set('2', KeyState.PRESSED);
    }
    
    // General case for number keys
    if (event.code.startsWith('Digit')) {
      const digit = event.code.substring(5); // Extract the digit
      if (this.keys.get(digit) !== KeyState.HELD) {
        this.keys.set(digit, KeyState.PRESSED);
      }
    }
    
    // Support for event.code (KeyW, KeyA, etc)
    if (event.code) {
      this.keys.set(event.code, KeyState.PRESSED);
    }
  }
  
  private onKeyUp(event: KeyboardEvent): void {
    const key = event.key.toLowerCase();
    this.keys.set(key, KeyState.RELEASED);
    
    // Special case for space key
    if (event.code === 'Space') {
      this.keys.set('space', KeyState.RELEASED);
    }
    
    // Special cases for number keys
    if (event.key === '1' || event.code === 'Digit1') {
      this.keys.set('1', KeyState.RELEASED);
    }
    
    if (event.key === '2' || event.code === 'Digit2') {
      this.keys.set('2', KeyState.RELEASED);
    }
    
    // General case for number keys
    if (event.code.startsWith('Digit')) {
      const digit = event.code.substring(5); // Extract the digit
      this.keys.set(digit, KeyState.RELEASED);
    }
    
    // Support for event.code (KeyW, KeyA, etc)
    if (event.code) {
      this.keys.set(event.code, KeyState.RELEASED);
    }
  }
  
  private onMouseMove(event: MouseEvent): void {
    this.mousePosition = { x: event.clientX, y: event.clientY };
    this.mouseMovement = { x: event.movementX, y: event.movementY };
  }
  
  private onMouseDown(event: MouseEvent): void {
    if (this.mouseButtons.get(event.button) !== KeyState.HELD) {
      this.mouseButtons.set(event.button, KeyState.PRESSED);
    }
  }
  
  private onMouseUp(event: MouseEvent): void {
    this.mouseButtons.set(event.button, KeyState.RELEASED);
  }
  
  private onWheel(event: WheelEvent): void {
    this.wheelDelta = event.deltaY;
  }
  
  public isKeyPressed(key: string): boolean {
    return this.keys.get(key.toLowerCase()) === KeyState.PRESSED;
  }
  
  public isKeyHeld(key: string): boolean {
    const state = this.keys.get(key.toLowerCase());
    return state === KeyState.PRESSED || state === KeyState.HELD;
  }
  
  public isKeyReleased(key: string): boolean {
    return this.keys.get(key.toLowerCase()) === KeyState.RELEASED;
  }
  
  public isMouseButtonPressed(button: number): boolean {
    return this.mouseButtons.get(button) === KeyState.PRESSED;
  }
  
  public isMouseButtonHeld(button: number): boolean {
    const state = this.mouseButtons.get(button);
    return state === KeyState.PRESSED || state === KeyState.HELD;
  }
  
  public isMouseButtonReleased(button: number): boolean {
    return this.mouseButtons.get(button) === KeyState.RELEASED;
  }
  
  public getMousePosition(): { x: number, y: number } {
    return { ...this.mousePosition };
  }
  
  public getMouseMovement(): { x: number, y: number } {
    return { ...this.mouseMovement };
  }
  
  public getWheelDelta(): number {
    return this.wheelDelta;
  }
  
  /**
   * Simulate a key being held down - used for mobile controls
   * @param key The key to simulate holding
   */
  public simulateKeyHold(key: string): void {
    const lowerKey = key.toLowerCase();
    if (this.keys.get(lowerKey) !== KeyState.HELD) {
      this.keys.set(lowerKey, KeyState.HELD);
    }
  }

  /**
   * Simulate a key being released - used for mobile controls
   * @param key The key to simulate releasing
   */
  public simulateKeyRelease(key: string): void {
    const lowerKey = key.toLowerCase();
    if (this.keys.get(lowerKey) === KeyState.HELD || this.keys.get(lowerKey) === KeyState.PRESSED) {
      this.keys.set(lowerKey, KeyState.RELEASED);
    }
  }
}